import { useConfigContext } from "@app/providers/config"
import { useCallback, useState } from "react"

export const useFunctions = (initialData = {}) => {
  const { store } = useConfigContext()
  const [data, setData] = useState(initialData)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Array<string>>([])

  // Subfolder routing (/au, /us) is only needed on the shared production domain (allkinds.com).
  // On Netlify deploy previews each region is its own site, so functions are at /api/* directly.
  const isDeployPreview = typeof window !== "undefined" && window.location.hostname.endsWith(".netlify.app")
  const subfolder = isDeployPreview ? "" : store.subfolder || ""

  const handleChange = ({ target: { type, name, value, checked } }: React.ChangeEvent<HTMLInputElement>) =>
    setData(prevState => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }))

  const callFunction = useCallback(
    async (name: string, data = {}) => {
      try {
        setLoading(true)
        setErrors([])

        const baseUrl =
          process.env.GATSBY_FUNCTIONS_DOMAIN || (typeof window !== "undefined" ? `${window.location.origin}${subfolder}` : "")
        const response = await fetch(`${baseUrl}/api/${name}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        })

        const body = await response.json()

        if (!response.ok) {
          const errorMessage = body?.body ? body.body : "An error occurred while processing the request."
          setErrors(prevErrors => [...prevErrors, errorMessage])
          setLoading(false)
          return { status: "error", body: errorMessage }
        }

        setLoading(false)
        return { ...body }
      } catch (err) {
        setLoading(false)
        setErrors([(err as Error).message])
        return { status: "error", body: (err as Error).message }
      }
    },
    [subfolder]
  )

  const getFunction = useCallback(
    async (name: string) => {
      try {
        setLoading(true)
        setErrors([])

        const baseUrl =
          process.env.GATSBY_FUNCTIONS_DOMAIN || (typeof window !== "undefined" ? `${window.location.origin}${subfolder}` : "")
        const response = await fetch(`${baseUrl}/api/${name}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        })

        const body = await response.json()

        setLoading(false)

        if (!response.ok) {
          const errorMessage = body?.body ? body.body : "An error occurred while processing the request."
          setErrors(prevErrors => [...prevErrors, errorMessage])
          return { status: "error", body: errorMessage }
        }

        return { ...body }
      } catch (err) {
        setLoading(false)
        const errorMessage = (err as Error).message
        setErrors([errorMessage])
        return { status: "error", body: errorMessage }
      }
    },
    [subfolder]
  )

  return { callFunction, getFunction, loading, errors, data, handleChange }
}
